import { CommonModule, Location } from "@angular/common";
import { Component, OnInit, OnDestroy, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CdkDragDrop, DragDropModule } from "@angular/cdk/drag-drop";
import { MatButtonModule } from "@angular/material/button";
import { MatChipsModule } from "@angular/material/chips";
import { MatDialog } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { FormsModule } from "@angular/forms";
import { MomentModule } from "ngx-moment";
import { merge, Subscription } from "rxjs";
import { debounceTime, filter, map, skip } from "rxjs/operators";

import {
  CoreComparisonDto,
  DesignerApplyRequest,
  DesignerJobScheduleDto,
  DesignerLayoutDto,
  DesignerScheduleOperationDto,
  DesignerTicketDto,
  DesignerVisitDto,
  DesignerVisitOperationDto,
  DesignerVisitScheduleDto,
  VisitStatus
} from "@app/services/api/v1/visitDesigner/dto/designer-dto";
import { VisitDesignerService } from "@app/services/api/v1/visitDesigner/visit-designer.service";
import {
  QUANTITY_EPSILON,
  QuantityDragDialogComponent,
  QuantityDragDialogData,
  QuantityDragDialogResult
} from "@app/view/visit-designer/quantity-drag-dialog/quantity-drag-dialog.component";
import { VisitTypeChooserDialogComponent } from "@app/view/shared/visit-type-chooser-dialog/visit-type-chooser-dialog.component";
import { TicketActionDialogComponent } from "@app/view/planning/ticket-action-dialog/ticket-action-dialog.component";
import { VisitTicketEditorComponent } from "@app/view/planning/visit-ticket-editor/visit-ticket-editor.component";
import { VisitBookingButtonComponent } from "@app/view/shared/visit-booking-button/visit-booking-button.component";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { VisitType } from "@app/models/rest/visit-type";
import { firstValueFrom } from "rxjs";
import { TicketStreamingStore } from "@app/services/streaming/ticket-streaming-store";
import { StompRouterService } from "@app/services/webstomp/stomp-router.service";
import { EntityChangeEvent } from "@app/services/webstomp/entity-change-event";
import { SolverService } from "@app/services/rest/solver.service";
import { NewSolutionEvent } from "@app/models/dto/new-solution-event";
import { JobDescriptionWidgetComponent } from "@app/integrations/urm/views/job-description-widget/job-description-widget.component";
import { JobContactWidgetComponent } from "@app/integrations/urm/views/job-contact-widget/job-contact-widget.component";
import { PropertyInfoComponent } from "@app/integrations/urm/views/property-info/property-info.component";
import { CautionWidgetComponent } from "@app/integrations/urm/views/caution-widget/caution-widget.component";
import { CoreLaunchWidgetComponent } from "@app/integrations/urm/views/core-launch-widget/core-launch-widget.component";

interface ScheduleDragPayload {
  /** Existing visit-schedule row being moved between lanes. */
  schedule: DesignerVisitScheduleDto;
  /** Source visit the row currently belongs to. */
  sourceVisit: DesignerVisitDto;
}

interface JobScheduleDragPayload {
  /** Marker so the lane drop handler can distinguish a JR drag from a lane-to-lane drag. */
  kind: "job-schedule";
  jobSchedule: DesignerJobScheduleDto;
}

type DesignerDragPayload = ScheduleDragPayload | JobScheduleDragPayload;

function isJobScheduleDrag(
  p: DesignerDragPayload
): p is JobScheduleDragPayload {
  return (p as JobScheduleDragPayload).kind === "job-schedule";
}

/**
 * One vertical slice in the designer's "bookshelf" layout. When collapsed, only the slice
 * spine is rendered (rotated label + chevron + count); when expanded, the slice grows to
 * its normal width and renders its contents.
 */
export interface DesignerSlice {
  /** Stable key used for collapse state and *ngFor tracking. */
  key: string;
  /**
   * Either a left-side context panel, a status-grouped batch of visit cards (vertical
   * view mode) or a single-visit slice (horizontal view mode).
   */
  kind: "panel" | "visit-group" | "visit";
  label: string;
  /** Optional badge value rendered on the spine (e.g. count of visits in the group). */
  count?: number;
  /** Visit cards inside a {@code visit-group} slice; absent for panel slices. */
  visits?: DesignerVisitDto[];
  /**
   * Status group key (e.g. {@code g-booked}) for spine-colour selection. Set on visit-group
   * slices and individual visit slices so the SCSS palette rules apply uniformly to both
   * view modes.
   */
  groupKey?: string;
  /** Single visit, present only on {@code kind: "visit"} slices. */
  visit?: DesignerVisitDto;
}

/** View mode for the visit area of the bookshelf. Job/JR/Comparison panels are unaffected. */
export type DesignerViewMode = "horizontal" | "vertical";

/**
 * Top-level Visit Designer board.
 *
 * Renders one column ("lane") per visit on a job, with a header row showing the underlying
 * job-schedule totals and an optional Core comparison panel. The user reshapes visits by:
 *
 * <ul>
 *   <li>Dragging a schedule from one visit to another (opens the quantity dialog)</li>
 *   <li>Pressing "Add Visit" to stage a new NEW-status visit (visit type picked via dialog)</li>
 *   <li>Pressing "Create follow-on" on a visit whose tickets requested follow-up work</li>
 *   <li>Pressing "Apply" to POST all staged operations atomically</li>
 * </ul>
 *
 * <p>All edits are staged client-side until {@link #apply()} is called; until then the layout is
 * mutated optimistically so the UI is responsive without round-tripping the server.
 */
@Component({
  selector: "app-visit-designer-board",
  standalone: true,
  templateUrl: "./visit-designer-board.component.html",
  styleUrls: ["./visit-designer-board.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    MatButtonModule,
    MatChipsModule,
    MatDividerModule,
    MatExpansionModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    MatTooltipModule,
    MomentModule,
    VisitBookingButtonComponent,
    JobDescriptionWidgetComponent,
    JobContactWidgetComponent,
    PropertyInfoComponent,
    CautionWidgetComponent,
    CoreLaunchWidgetComponent
  ]
})
export class VisitDesignerBoardComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly designerService = inject(VisitDesignerService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly clientConfig = inject(ClientConfigService);
  private readonly stompRouter = inject(StompRouterService);
  private readonly solverService = inject(SolverService);
  /**
   * Injected for its constructor side effect only: instantiating the store registers the
   * /topic/ticket/* STOMP subscriptions that feed the "Ticket" entity stream consumed in
   * {@link subscribeToLiveUpdates}.
   */
  private readonly ticketStreamingStore = inject(TicketStreamingStore);

  get urmEnabled(): boolean {
    return !!this.clientConfig?.config?.urmServices?.enabled;
  }

  /** True when the job is an Orchestrator/project job — visits must not be edited. */
  get isProject(): boolean {
    return !!this.layout?.externalProjectReference;
  }

  /** True when the designer should be fully read-only (closed job or orchestrator project). */
  get isReadOnly(): boolean {
    return this.layout?.jobStatus === "CLOSED" || this.isProject;
  }

  jobId = 0;
  layout: DesignerLayoutDto | undefined;
  comparison: CoreComparisonDto | undefined;

  loading = false;
  applying = false;

  /** Pending visit operations (in order). */
  visitOps: DesignerVisitOperationDto[] = [];
  /** Pending schedule operations (in order). */
  scheduleOps: DesignerScheduleOperationDto[] = [];

  /** Sequence used to generate unique clientRefs for newly-staged visits. */
  private newVisitSeq = 0;

  /**
   * Quiet period applied to the STOMP entity streams before re-fetching the layout. A single
   * booking or apply produces a burst of related ticket/visit messages; one debounced
   * {@link refresh} picks them all up.
   */
  private static readonly LIVE_REFRESH_DEBOUNCE_MS = 500;

  private readonly liveUpdateSubscriptions = new Subscription();

  /**
   * True once the user has been told the job changed elsewhere while they had staged ops.
   * Prevents a snackbar per message; reset when the layout is re-fetched.
   */
  private externalChangeNotified = false;

  // --- bookshelf "slice" state -------------------------------------------
  /**
   * Per-slice collapsed state (true == only the spine is shown). Defaults are seeded the
   * first time a slice is rendered (see {@link initSliceDefaults}). Pure in-memory state.
   */
  sliceCollapsed: Record<string, boolean> = {};
  /** Tracks which slice keys we've already seeded a default for. */
  private seededDefaults = new Set<string>();

  /** Currently hovered job-schedule id, used to cross-highlight matching rows. */
  hoveredJobScheduleId: number | null = null;

  /**
   * View mode for the visit-cards area. {@code horizontal} (default) puts every visit in its
   * own slice/column with its own collapsible spine, ordered by status group. {@code vertical}
   * groups visits by status into a single slice each, stacked vertically inside the body
   * (the old behaviour). Job / Job Requirements / Core Comparison panels are unaffected.
   */
  viewMode: DesignerViewMode = "vertical";

  /** localStorage key used to persist {@link viewMode} across designer visits. */
  private static readonly VIEW_MODE_LS_KEY = "visit-designer.viewMode";

  toggleViewMode(): void {
    this.viewMode = this.viewMode === "horizontal" ? "vertical" : "horizontal";
    this.persistViewMode();
  }

  private persistViewMode(): void {
    try {
      window.localStorage?.setItem(
        VisitDesignerBoardComponent.VIEW_MODE_LS_KEY,
        this.viewMode
      );
    } catch {
      /* localStorage unavailable (private mode / quota) — fail silently. */
    }
  }

  private restoreViewMode(): void {
    try {
      const saved = window.localStorage?.getItem(
        VisitDesignerBoardComponent.VIEW_MODE_LS_KEY
      );
      if (saved === "horizontal" || saved === "vertical") {
        this.viewMode = saved;
      }
    } catch {
      /* localStorage unavailable — keep default. */
    }
  }

  ngOnInit(): void {
    this.restoreViewMode();
    const param = this.route.snapshot.paramMap.get("jobId");
    this.jobId = Number(param);
    if (!this.jobId || Number.isNaN(this.jobId)) {
      this.snackBar.open("No jobId provided", "Dismiss", { duration: 3000 });
      return;
    }
    this.subscribeToLiveUpdates();
    void this.refresh();
  }

  ngOnDestroy(): void {
    this.liveUpdateSubscriptions.unsubscribe();
  }

  // --- data ---------------------------------------------------------------

  /** True while a layout fetch is in flight (foreground or background), used to dedupe. */
  private fetchingLayout = false;

  /**
   * Re-fetch the job layout.
   *
   * @param background when true, do NOT show the full-screen loading spinner. The bookshelf
   *   stays mounted and Angular reconciles the new layout in place (the `@for` loops track by
   *   stable ids/keys), so the user's scroll position and expand/collapse state are preserved
   *   and only the changed ticket cards repaint. Used for live STOMP/solver-driven updates;
   *   foreground (spinner) is kept for the initial load and explicit user reloads, where there
   *   is no meaningful position to preserve.
   */
  async refresh(background = false): Promise<void> {
    this.fetchingLayout = true;
    if (!background) {
      this.loading = true;
    }
    try {
      this.layout = await this.designerService.getLayout(this.jobId);
      this.visitOps = [];
      this.scheduleOps = [];
      this.newVisitSeq = 0;
      this.externalChangeNotified = false;
      void this.loadComparison();
    } catch {
      this.snackBar.open("Failed to load designer layout", "Dismiss", {
        duration: 4000
      });
    } finally {
      this.fetchingLayout = false;
      if (!background) {
        this.loading = false;
      }
    }
  }

  // --- live updates ---------------------------------------------------------

  /**
   * React in real time when this job's tickets or visits change elsewhere — another user
   * booking or rebooking a visit, a resource lock applied from the ticket dialog, an
   * operative progressing a ticket, or the SOLVER reassigning a ticket to a different
   * operative. The server publishes user/operative-driven changes over the Ticket/Visit
   * entity STOMP streams, but deliberately SUPPRESSES solver moves on those streams while
   * a ticket is PLANNING (see TicketChangeListener) to avoid message storms — solver moves
   * instead arrive via the solver status stream as a NewSolutionEvent. We listen on all
   * three, keep only changes that touch this job, debounce the burst a single change
   * produces and re-fetch the layout (it carries server-derived state, so re-fetching is
   * the only safe way to apply a change).
   */
  private subscribeToLiveUpdates(): void {
    // Idempotent: ensures the Visit topic is routed even if nothing else subscribed it yet.
    this.stompRouter.addSubscription("/topic/Visit");
    const solverChanges = this.solverService.updateStream.pipe(
      map((info) => info?.changeEvent),
      filter(
        (event): event is NewSolutionEvent =>
          !!event && this.solverEventTouchesThisJob(event)
      )
    );
    this.liveUpdateSubscriptions.add(
      merge(
        // skip(1): the entity streams are BehaviorSubjects, so they replay their most
        // recent (pre-navigation, stale) event on subscribe.
        this.stompRouter.getEntityStream("Ticket").pipe(
          skip(1),
          filter((event) => !!event && this.eventTouchesThisJob(event))
        ),
        this.stompRouter.getEntityStream("Visit").pipe(
          skip(1),
          filter((event) => !!event && this.eventTouchesThisJob(event))
        ),
        solverChanges
      )
        .pipe(
          debounceTime(VisitDesignerBoardComponent.LIVE_REFRESH_DEBOUNCE_MS)
        )
        .subscribe(() => this.onExternalChange())
    );
  }

  /**
   * True when a solver solution delta reassigned, added or removed a ticket belonging to
   * this job. Solver ticket DTOs carry {@code externalJobRef} (matched against the layout's
   * job reference) and {@code ticketId} (matched against the tickets currently displayed),
   * so a move of any of this job's tickets triggers a refresh.
   */
  private solverEventTouchesThisJob(event: NewSolutionEvent): boolean {
    const jobRef = this.layout?.externalJobReference;
    const displayedTicketIds = new Set(
      (this.layout?.visits ?? []).flatMap((v) =>
        (v.tickets ?? []).map((t) => t.id)
      )
    );
    return [
      ...(event.added ?? []),
      ...(event.updated ?? []),
      ...(event.removed ?? [])
    ].some(
      (dto) =>
        (jobRef != null && dto.externalJobRef === jobRef) ||
        displayedTicketIds.has(dto.ticketId)
    );
  }

  /**
   * True when a STOMP entity change event concerns the job on this board. Ticket events
   * carry {@code visit.job.id} and Visit events carry {@code job.id} in their projection
   * payload; when the payload is missing or shallow (e.g. DELETE events fall back to the
   * bare entity) we match the event id against what the board is currently showing.
   */
  private eventTouchesThisJob(event: EntityChangeEvent): boolean {
    const payload = event.payload as {
      id?: number;
      job?: { id?: number };
      visit?: { id?: number; job?: { id?: number } };
    } | null;
    const jobId =
      event.entity === "Visit" ? payload?.job?.id : payload?.visit?.job?.id;
    if (jobId != null) {
      return jobId === this.jobId;
    }
    const visits = this.layout?.visits ?? [];
    if (event.entity === "Visit") {
      return visits.some((v) => v.id === event.id);
    }
    const visitId = payload?.visit?.id;
    if (visitId != null) {
      return visits.some((v) => v.id === visitId);
    }
    return visits.some((v) => (v.tickets ?? []).some((t) => t.id === event.id));
  }

  /**
   * A relevant external change arrived. Re-fetch the layout unless the user has staged
   * ops — refreshing discards them, so in that case surface a snackbar and let the user
   * choose (Apply will be validated server-side; Reload abandons the staged work).
   */
  private onExternalChange(): void {
    if (this.fetchingLayout || this.applying) {
      return;
    }
    if (this.hasPending()) {
      if (!this.externalChangeNotified) {
        this.externalChangeNotified = true;
        this.snackBar
          .open("This job was changed by someone else.", "Reload", {
            duration: 8000
          })
          .onAction()
          .subscribe(() => void this.refresh(true));
      }
      return;
    }
    // Background refresh: keep the bookshelf mounted so scroll position and expand state
    // survive; only the changed cards repaint.
    void this.refresh(true);
  }

  async loadComparison(): Promise<void> {
    try {
      this.comparison = await this.designerService.getCoreComparison(
        this.jobId
      );
    } catch {
      this.comparison = undefined;
    }
  }

  // --- view helpers -------------------------------------------------------

  get visibleVisits(): DesignerVisitDto[] {
    if (!this.layout) {
      return [];
    }
    // Cancelled visits remain visible in their (default-collapsed) spine so the user
    // can always pop them open; the old "show cancelled" toggle is gone.
    return this.layout.visits;
  }

  visibleSchedules(visit: DesignerVisitDto): DesignerVisitScheduleDto[] {
    // Archived ghost rows are part of the visit's audit trail; always render them.
    return visit.schedules;
  }

  laneDropListIds(): string[] {
    return [
      ...this.visibleVisits.map((v) => this.dropListId(v)),
      this.jobRequirementsDropListId()
    ];
  }

  /** Drop-list id for the left-panel job-requirements source list. */
  jobRequirementsDropListId(): string {
    return "job-requirements";
  }

  /**
   * Connected drop-list ids exposed on the JR source list - it must be connected to every
   * lane so dragging a requirement card onto any lane is accepted.
   */
  jobRequirementsConnectedTo(): string[] {
    return this.laneDropListIds();
  }

  dropListId(visit: DesignerVisitDto): string {
    return visit.id != null ? `visit-${visit.id}` : `new-${visit.clientRef}`;
  }

  /**
   * True when moving requirements off this visit must leave an archived ghost behind
   * (i.e. the source visit is historical and its allocation is part of the audit trail).
   * Only {@code COMPLETED} and {@code CANCELLED} visits archive; {@code BOOKED} and
   * {@code IN_PROGRESS} are locked for editing but do NOT support archive-on-move - they
   * must be referred first.
   */
  sourceWillArchive(visit: DesignerVisitDto): boolean {
    return (
      visit.visitStatus === "COMPLETED" || visit.visitStatus === "CANCELLED"
    );
  }

  /**
   * True if requirement rows can be dragged off this visit at all.
   * <ul>
   *   <li>Editable visits (NEW/AUTO/FLOATING/REFERRED): yes - normal move.</li>
   *   <li>Completed/Cancelled: yes - leaves an archived ghost.</li>
   *   <li>Booked/In-progress: no - the visit must be referred before its
   *       requirements can be reshaped.</li>
   * </ul>
   */
  canDragFrom(visit: DesignerVisitDto): boolean {
    if (this.isReadOnly) return false;
    if (this.sourceWillArchive(visit)) {
      return true;
    }
    return !visit.locked;
  }

  /** Tooltip explaining why a row is not draggable. */
  dragBlockedReason(visit: DesignerVisitDto): string {
    if (visit.visitStatus === "BOOKED") {
      return "Booked visits are locked. Refer the visit before reshaping its requirements.";
    }
    if (visit.visitStatus === "IN_PROGRESS") {
      return "In-progress visits are locked.";
    }
    return visit.lockReason ?? "Locked";
  }

  hasPending(): boolean {
    return this.visitOps.length > 0 || this.scheduleOps.length > 0;
  }

  /** Active (non-archived, quantity > 0) schedule rows on a visit. */
  activeSchedules(visit: DesignerVisitDto): DesignerVisitScheduleDto[] {
    return visit.schedules.filter(
      (s) => !s.archivedGhost && (s.quantity ?? 0) > QUANTITY_EPSILON
    );
  }

  /**
   * Detect the "this visit reported that follow-on work is needed but nobody has scheduled
   * the follow-on yet" condition. Drives the visibility of the {@code Create follow-on} button.
   */
  needsFollowOn(visit: DesignerVisitDto): boolean {
    if (!visit.tickets) return false;
    return visit.tickets.some(
      (t) => t.followOnRequired === true && t.followOnActioned !== true
    );
  }

  /**
   * Lower-case CSS-friendly status string for chip styling.
   * E.g. {@code IN_PROGRESS} -> {@code in-progress}.
   */
  /** CSS group key for a visit (used for `data-group-key` in vertical list rows). */
  visitGroupKey(visit: DesignerVisitDto): string {
    if (this.needsFollowOn(visit)) return "g-follow-on";
    switch (visit.visitStatus) {
      case "CANCELLED":
        return "g-cancelled";
      case "COMPLETED":
        return "g-completed";
      case "IN_PROGRESS":
        return "g-in-progress";
      case "BOOKED":
        return "g-booked";
      case "FLOATING":
        return "g-floating";
      default:
        return "g-active";
    }
  }

  statusClass(status: string | undefined): string {
    return (status ?? "").toLowerCase().replace(/_/g, "-");
  }

  /**
   * Summary row data for vertical-mode visit list. Returns the time range string
   * ("HH:mm – HH:mm" on the same day, or "DD Mon HH:mm – HH:mm" across days) plus a
   * flag indicating whether the visit is scheduled at all.
   */
  visitTimeRange(visit: DesignerVisitDto): {
    label: string;
    scheduled: boolean;
  } {
    if (!visit.appointmentStart) {
      return { label: "Not scheduled", scheduled: false };
    }
    const start = new Date(visit.appointmentStart);
    if (isNaN(start.getTime()))
      return { label: "Not scheduled", scheduled: false };
    const fmt = (d: Date) =>
      d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const dateStr = start.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short"
    });
    if (!visit.appointmentEnd)
      return { label: `${dateStr} ${fmt(start)}`, scheduled: true };
    const end = new Date(visit.appointmentEnd);
    return { label: `${dateStr} ${fmt(start)} – ${fmt(end)}`, scheduled: true };
  }

  /**
   * 0–1 fraction of schedule SMV covered by tickets for the visit summary progress bar.
   * Returns 0 when there are no schedules.
   */
  visitScheduleFraction(visit: DesignerVisitDto): number {
    const scheduled = this.scheduleDurationMinutes(visit);
    if (scheduled <= 0) return 0;
    const planned = this.plannedDurationTotalMinutes(visit);
    if (planned <= 0) return 0;
    return Math.min(1, scheduled / planned);
  }

  /** Sum of planned (pre-despatch) or snapshotted ticket durations; 0 when none. */
  plannedDurationTotalMinutes(visit: DesignerVisitDto): number {
    let total = 0;
    for (const t of visit.tickets ?? []) {
      const m = this.plannedMinutesFor(t);
      if (m != null) total += m;
    }
    return total;
  }

  /** Ordinal index of a visit within the visible visits list (1-based). */
  visitIndex(visit: DesignerVisitDto): number {
    return this.visibleVisits.indexOf(visit) + 1;
  }

  /** CSS class suffix for the numbered badge colour in the vertical visit list. */
  visitBadgeClass(visit: DesignerVisitDto): string {
    return this.statusClass(visit.visitStatus);
  }

  /**
   * Human-friendly appointment summary for a visit lane header. Returns {@code null}
   * for visits with no booked appointment so the template can omit the row entirely.
   *
   * <p>The {@code label} is short ("Mon 12 May, 09:30") for the inline header; the
   * {@code tooltip} carries the full start-end range for hover detail.
   */
  appointmentLabel(
    visit: DesignerVisitDto
  ): { label: string; tooltip: string } | null {
    if (!visit.appointmentStart) {
      return null;
    }
    const start = new Date(visit.appointmentStart);
    if (isNaN(start.getTime())) {
      return null;
    }
    const dateOpts: Intl.DateTimeFormatOptions = {
      weekday: "short",
      day: "2-digit",
      month: "short"
    };
    const timeOpts: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    };
    const label = `${start.toLocaleDateString(undefined, dateOpts)}, ${start.toLocaleTimeString(undefined, timeOpts)}`;
    let tooltip = `Appointment: ${start.toLocaleString()}`;
    if (visit.appointmentEnd) {
      const end = new Date(visit.appointmentEnd);
      if (!isNaN(end.getTime())) {
        tooltip = `Appointment: ${start.toLocaleString()} \u2192 ${end.toLocaleTimeString(undefined, timeOpts)}`;
      }
    }
    return { label, tooltip };
  }

  // --- staging actions ----------------------------------------------------

  /**
   * Add a brand-new visit. Prompts the user to pick a {@link VisitType} via the shared
   * chooser dialog, then stages a CREATE op carrying the chosen type id and description.
   * Cancelling the picker is a no-op.
   */
  async addVisit(): Promise<void> {
    if (!this.layout) return;
    const visitType = await this.pickVisitType();
    if (!visitType) return;
    this.newVisitSeq += 1;
    const clientRef = `new-${this.newVisitSeq}`;
    const newVisit: DesignerVisitDto = {
      clientRef,
      visitStatus: "NEW",
      visitTypeId: visitType.id,
      visitTypeDescription: visitType.description,
      locked: false,
      schedules: [],
      tickets: []
    };
    this.layout.visits.push(newVisit);
    this.visitOps.push({
      op: "CREATE",
      clientRef,
      visitStatus: "NEW",
      visitTypeId: visitType.id
    });
  }

  /**
   * Stage a follow-on visit cloned from {@code source}. The user picks a visit type, then
   * each active schedule row on the source is:
   * <ul>
   *   <li>copied to a new staged visit (full quantity), and</li>
   *   <li>archived on the source (ARCHIVE op with the same {@code carriedForwardQuantity}).</li>
   * </ul>
   * The new visit's {@code parentVisitId} is set to the source visit's id so the server can
   * stitch the lineage. Cancelling the picker is a no-op.
   */
  async createFollowOn(source: DesignerVisitDto): Promise<void> {
    if (!this.layout) return;
    if (source.id == null) {
      this.snackBar.open(
        "Apply your changes before cloning this visit.",
        "Dismiss",
        { duration: 4000 }
      );
      return;
    }
    const active = this.activeSchedules(source);
    if (active.length === 0) {
      this.snackBar.open(
        "This visit has no active schedules to carry forward.",
        "Dismiss",
        { duration: 4000 }
      );
      return;
    }
    const visitType = await this.pickVisitType();
    if (!visitType) return;

    this.newVisitSeq += 1;
    const clientRef = `new-${this.newVisitSeq}`;
    const followOn: DesignerVisitDto = {
      clientRef,
      visitStatus: "NEW",
      visitTypeId: visitType.id,
      visitTypeDescription: visitType.description,
      parentVisitId: source.id,
      locked: false,
      schedules: [],
      tickets: []
    };
    this.layout.visits.push(followOn);
    this.visitOps.push({
      op: "CREATE",
      clientRef,
      visitStatus: "NEW",
      visitTypeId: visitType.id,
      parentVisitId: source.id
    });

    // Carry each active schedule over: archive on source, create on follow-on.
    for (const sch of active) {
      const moved = sch.quantity ?? 0;
      sch.archivedGhost = true;
      sch.quantity = 0;
      sch.archivedQuantity = (sch.archivedQuantity ?? 0) + moved;
      this.scheduleOps.push({
        op: "ARCHIVE",
        visitScheduleId: sch.id,
        archivedQuantity: sch.archivedQuantity,
        carriedForwardQuantity: moved
      });
      const newRow: DesignerVisitScheduleDto = {
        jobScheduleId: sch.jobScheduleId,
        sorCode: sch.sorCode,
        sorDescription: sch.sorDescription,
        sorSmv: sch.sorSmv,
        externalScheduleReference: sch.externalScheduleReference,
        quantity: moved
      };
      followOn.schedules.push(newRow);
      this.scheduleOps.push({
        op: "SET_QUANTITY",
        jobScheduleId: sch.jobScheduleId,
        targetVisitClientRef: clientRef,
        quantity: moved
      });
    }
  }

  removeStagedVisit(visit: DesignerVisitDto): void {
    if (!this.layout) return;
    if (visit.schedules.some((s) => (s.quantity ?? 0) > QUANTITY_EPSILON)) {
      this.snackBar.open(
        "Move active schedules off this visit before deleting it.",
        "Dismiss",
        { duration: 4000 }
      );
      return;
    }
    this.layout.visits = this.layout.visits.filter((v) => v !== visit);
    if (visit.id != null) {
      this.visitOps.push({ op: "DELETE", visitId: visit.id });
    } else {
      // Remove the corresponding CREATE op so we don't create-then-delete on the server.
      this.visitOps = this.visitOps.filter(
        (o) => !(o.op === "CREATE" && o.clientRef === visit.clientRef)
      );
    }
  }

  async drop(
    event: CdkDragDrop<DesignerVisitDto, DesignerVisitDto, DesignerDragPayload>
  ): Promise<void> {
    if (!this.layout) return;
    const payload = event.item.data;
    const targetVisit = event.container.data;
    if (isJobScheduleDrag(payload)) {
      await this.dropJobRequirement(targetVisit, payload.jobSchedule);
      return;
    }
    const { schedule, sourceVisit } = payload;
    if (sourceVisit === targetVisit) {
      return;
    }
    const result = await this.openQuantityDialog(
      sourceVisit,
      targetVisit,
      schedule
    );
    if (!result) {
      return;
    }
    this.applyStagedMove(sourceVisit, targetVisit, schedule, result);
  }

  private async openQuantityDialog(
    source: DesignerVisitDto,
    target: DesignerVisitDto,
    schedule: DesignerVisitScheduleDto
  ): Promise<QuantityDragDialogResult | undefined> {
    const data: QuantityDragDialogData = {
      source,
      target,
      schedule,
      sourceWillArchive: this.sourceWillArchive(source)
    };
    const ref = this.dialog.open<
      QuantityDragDialogComponent,
      QuantityDragDialogData,
      QuantityDragDialogResult
    >(QuantityDragDialogComponent, { data });
    return firstValueFrom(ref.afterClosed());
  }

  /**
   * Open the shared visit-type chooser. Resolves to the chosen {@link VisitType} or
   * {@code undefined} if the user dismissed the dialog.
   */
  private async pickVisitType(): Promise<VisitType | undefined> {
    const ref = this.dialog.open<
      VisitTypeChooserDialogComponent,
      unknown,
      VisitType
    >(VisitTypeChooserDialogComponent, {
      data: { resource: {} },
      width: "640px"
    });
    const chosen = await firstValueFrom(ref.afterClosed());
    return chosen ?? undefined;
  }

  /** Mutates the layout optimistically and records the equivalent backend operations. */
  private applyStagedMove(
    sourceVisit: DesignerVisitDto,
    targetVisit: DesignerVisitDto,
    schedule: DesignerVisitScheduleDto,
    result: QuantityDragDialogResult
  ): void {
    const sourceActive = schedule.quantity ?? 0;
    const moved = result.quantity;
    const remaining = sourceActive - moved;

    // Source side
    if (result.sourceWillArchive) {
      schedule.archivedGhost = true;
      schedule.quantity = 0;
      schedule.archivedQuantity = (schedule.archivedQuantity ?? 0) + moved;
      this.scheduleOps.push({
        op: "ARCHIVE",
        visitScheduleId: schedule.id,
        archivedQuantity: schedule.archivedQuantity,
        carriedForwardQuantity: moved
      });
    } else if (remaining > QUANTITY_EPSILON) {
      schedule.quantity = remaining;
      this.scheduleOps.push({
        op: "SET_QUANTITY",
        visitScheduleId: schedule.id,
        quantity: remaining
      });
    } else {
      // Full move from a non-archiving source: drop the row from the source visit.
      sourceVisit.schedules = sourceVisit.schedules.filter(
        (s) => s !== schedule
      );
      if (schedule.id != null) {
        this.scheduleOps.push({
          op: "DELETE",
          visitScheduleId: schedule.id
        });
      }
    }

    // Target side: stack onto an existing matching schedule on the target, else create.
    const existing = targetVisit.schedules.find(
      (s) =>
        !s.archivedGhost &&
        s.jobScheduleId === schedule.jobScheduleId &&
        s.id != null
    );
    if (existing) {
      existing.quantity = (existing.quantity ?? 0) + moved;
      this.scheduleOps.push({
        op: "SET_QUANTITY",
        visitScheduleId: existing.id,
        quantity: existing.quantity
      });
    } else {
      const newRow: DesignerVisitScheduleDto = {
        jobScheduleId: schedule.jobScheduleId,
        visitId: targetVisit.id,
        sorCode: schedule.sorCode,
        sorDescription: schedule.sorDescription,
        sorSmv: schedule.sorSmv,
        externalScheduleReference: schedule.externalScheduleReference,
        quantity: moved
      };
      targetVisit.schedules.push(newRow);
      this.scheduleOps.push({
        op: "SET_QUANTITY",
        jobScheduleId: schedule.jobScheduleId,
        targetVisitId: targetVisit.id,
        targetVisitClientRef: targetVisit.clientRef,
        quantity: moved
      });
    }
  }

  // --- commit -------------------------------------------------------------

  async apply(): Promise<void> {
    if (!this.hasPending() || !this.layout) {
      return;
    }
    this.applying = true;
    const request: DesignerApplyRequest = {
      jobId: this.jobId,
      visitOperations: [...this.visitOps],
      scheduleOperations: [...this.scheduleOps]
    };
    try {
      this.layout = await this.designerService.apply(request);
      this.visitOps = [];
      this.scheduleOps = [];
      this.newVisitSeq = 0;
      this.externalChangeNotified = false;
      this.snackBar.open("Designer changes applied", "Dismiss", {
        duration: 2500
      });
      void this.loadComparison();
    } catch (err: unknown) {
      const errObj = err as {
        error?: unknown;
        message?: string;
      };
      const message =
        (typeof errObj.error === "string" ? errObj.error : null) ??
        (errObj.error as { message?: string } | undefined)?.message ??
        errObj.message ??
        "Failed to apply changes";
      this.snackBar.open(message, "Dismiss", { duration: 6000 });
    } finally {
      this.applying = false;
    }
  }

  discard(): void {
    void this.refresh();
  }

  back(): void {
    this.location.back();
  }

  /** Used in templates to satisfy strict typing on optional ticket arrays. */
  ticketsOf(visit: DesignerVisitDto): DesignerTicketDto[] {
    return visit.tickets ?? [];
  }

  // --- bookshelf slices ---------------------------------------------------

  /**
   * Build the ordered list of bookshelf slices that the template renders left-to-right.
   * Empty visit groups are omitted entirely so the board doesn't display dead spines.
   *
   * <p>Order:
   * <ol>
   *   <li>{@code job} - job context panel</li>
   *   <li>{@code requirements} - draggable job-requirement mini-cards</li>
   *   <li>{@code coreComparison} - present only when Core data is available</li>
   *   <li>Visit groups in the order: cancelled, completed, follow-on, in-progress,
   *       booked, active (NEW/AUTO/FLOATING/REFERRED)</li>
   * </ol>
   */
  /**
   * Left "panels" region: job context, job requirements, optional core-comparison.
   * Always rendered as horizontal columns regardless of view mode.
   */
  panelSlices(): DesignerSlice[] {
    const out: DesignerSlice[] = [];
    if (this.layout) {
      out.push({
        kind: "panel",
        key: "job",
        label: "Job",
        count: undefined
      });
      out.push({
        kind: "panel",
        key: "requirements",
        label: "Job Requirements",
        count: this.layout.jobSchedules.length
      });
      if (this.comparison?.coreAvailable) {
        out.push({
          kind: "panel",
          key: "coreComparison",
          label: this.comparison.hasChanges
            ? "Core Comparison \u2022 changes"
            : "Core Comparison",
          count: undefined
        });
      }
    }
    this.applyDefaults(out);
    return out;
  }

  /**
   * Right "visits" region: one slice per visit, ordered by status group so coloured
   * bands stay grouped. Orientation (column vs row) is controlled in CSS via
   * {@code viewMode} on the bookshelf.
   */
  visitSlices(): DesignerSlice[] {
    const out: DesignerSlice[] = [];
    for (const g of this.visitGroups()) {
      for (const v of g.visits) {
        out.push({
          kind: "visit",
          key: this.visitSliceKey(v),
          label: this.visitSliceLabel(v),
          visit: v,
          groupKey: g.key
        });
      }
    }
    this.applyDefaults(out);
    return out;
  }

  /**
   * @deprecated Kept for tests / external callers; new code should use
   * {@link panelSlices} + {@link visitSlices} which split the bookshelf into
   * orientation-independent regions.
   */
  slices(): DesignerSlice[] {
    return [...this.panelSlices(), ...this.visitSlices()];
  }

  private applyDefaults(slices: DesignerSlice[]): void {
    for (const s of slices) {
      if (!this.seededDefaults.has(s.key)) {
        this.sliceCollapsed[s.key] = this.defaultCollapsed(s);
        this.seededDefaults.add(s.key);
      }
    }
  }

  /** Stable slice key for an individual visit (handles staged visits via clientRef). */
  private visitSliceKey(v: DesignerVisitDto): string {
    return v.id != null ? `v-${v.id}` : `v-new-${v.clientRef}`;
  }

  /**
   * Spine label for an individual visit slice: status + appointment date when available.
   * Readable at a glance in the vertical accordion (the visit type lives in the card header).
   */
  private visitSliceLabel(v: DesignerVisitDto): string {
    const status = this.visitStatusLabel(v.visitStatus, v.id == null);
    const date = v.appointmentStart
      ? new Date(v.appointmentStart).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short"
        })
      : null;
    return date ? `${status} · ${date}` : status;
  }

  visitStatusLabel(
    status: VisitStatus | undefined,
    isDraft: boolean = false
  ): string {
    if (isDraft && status === "NEW") {
      return "Draft";
    }
    switch (status) {
      case "NEW":
        return "New";
      case "FLOATING":
        return "Floating";
      case "BOOKED":
        return "Booked";
      case "IN_PROGRESS":
        return "In Progress";
      case "CANCELLED":
        return "Cancelled";
      case "COMPLETED":
        return "Completed";
      case "AUTO":
        return "Auto";
      case "REFERRED":
        return "Referred";
      default:
        return "Visit";
    }
  }

  /**
   * Buckets visible visits into status-themed groups. Follow-on-needed visits are pulled
   * out of their underlying status group into a dedicated pseudo-group so the user can
   * fold the rest of the status band away without losing visibility of pending follow-on
   * work.
   */
  visitGroups(): { key: string; label: string; visits: DesignerVisitDto[] }[] {
    const cancelled: DesignerVisitDto[] = [];
    const completed: DesignerVisitDto[] = [];
    const followOn: DesignerVisitDto[] = [];
    const inProgress: DesignerVisitDto[] = [];
    const booked: DesignerVisitDto[] = [];
    const floating: DesignerVisitDto[] = [];
    const active: DesignerVisitDto[] = [];
    for (const v of this.visibleVisits) {
      if (this.needsFollowOn(v)) {
        followOn.push(v);
        continue;
      }
      switch (v.visitStatus) {
        case "CANCELLED":
          cancelled.push(v);
          break;
        case "COMPLETED":
          completed.push(v);
          break;
        case "IN_PROGRESS":
          inProgress.push(v);
          break;
        case "BOOKED":
          booked.push(v);
          break;
        case "FLOATING":
          floating.push(v);
          break;
        default:
          // NEW / AUTO / REFERRED
          active.push(v);
          break;
      }
    }
    return [
      { key: "g-cancelled", label: "Cancelled", visits: cancelled },
      { key: "g-completed", label: "Completed", visits: completed },
      { key: "g-follow-on", label: "Needs Follow-on", visits: followOn },
      { key: "g-in-progress", label: "In Progress", visits: inProgress },
      { key: "g-booked", label: "Booked", visits: booked },
      { key: "g-floating", label: "Floating", visits: floating },
      { key: "g-active", label: "New / Referred", visits: active }
    ];
  }

  /** Default collapsed state for a freshly-seen slice. */
  private defaultCollapsed(slice: DesignerSlice): boolean {
    if (slice.kind === "visit-group") {
      return slice.key === "g-cancelled" || slice.key === "g-completed";
    }
    if (slice.kind === "visit") {
      // Cancelled / completed visit slices start collapsed in horizontal mode so they
      // don't dominate the board. Same noise-control logic as the vertical groups.
      return (
        slice.groupKey === "g-cancelled" || slice.groupKey === "g-completed"
      );
    }
    return false;
  }

  toggleSlice(key: string): void {
    this.sliceCollapsed[key] = !this.sliceCollapsed[key];
  }

  isSliceCollapsed(key: string): boolean {
    return !!this.sliceCollapsed[key];
  }

  /**
   * Icon name for the collapse/expand chevron on a slice spine. Panel slices (always rendered
   * as vertical columns) use left/right arrows. Visit slices in vertical accordion mode use
   * up/down arrows to match the row-expansion convention.
   */
  spineChevron(slice: DesignerSlice, collapsed: boolean): string {
    if (slice.kind === "visit" && this.viewMode === "vertical") {
      return collapsed ? "expand_more" : "expand_less";
    }
    return collapsed ? "chevron_right" : "chevron_left";
  }

  // --- cross-highlight ----------------------------------------------------

  /**
   * True when the given schedule row should be visually highlighted because the user is
   * hovering over either it or its matching job-requirement row in the left panel.
   */
  isHighlighted(jobScheduleId: number | undefined): boolean {
    return jobScheduleId != null && this.hoveredJobScheduleId === jobScheduleId;
  }

  // --- amend gating -------------------------------------------------------

  /**
   * Mirrors the predicate used by ticket-action-dialog: only NEW/BOOKED/FLOATING/REFERRED
   * visits may have their requirements amended via the editor dialog. Editing in-progress,
   * completed or cancelled visits requires referral first.
   */
  canAmend(visit: DesignerVisitDto): boolean {
    if (this.isReadOnly) return false;
    if (this.hasPending()) return false;
    return (
      visit.visitStatus === "NEW" ||
      visit.visitStatus === "BOOKED" ||
      visit.visitStatus === "FLOATING" ||
      visit.visitStatus === "REFERRED"
    );
  }

  amendRestrictionReason(visit: DesignerVisitDto): string {
    if (this.hasPending()) return "Apply or discard changes first";
    return `Visit status is ${visit.visitStatus}`;
  }

  /**
   * Stub for "open the visit ticket editor for this visit" - wired in a follow-up that
   * imports {@code VisitTicketEditorComponent} from the planning area. Today we just toast
   * so the click target exists and tests can verify gating.
   */
  amendRequirements(visit: DesignerVisitDto): void {
    if (!this.canAmend(visit)) return;
    if (visit.id == null) {
      this.snackBar.open(
        "Save the staged visit before editing requirements",
        "Dismiss",
        { duration: 2500 }
      );
      return;
    }
    const ref = this.dialog.open(VisitTicketEditorComponent, {
      width: "460px",
      data: { visitId: visit.id }
    });
    ref.afterClosed().subscribe((result) => {
      if (result) {
        // Editor returned saved tickets — reload layout to pick up duration
        // and requirement changes applied directly against the server.
        void this.refresh();
      }
    });
  }

  /**
   * Returns the configured background / foreground colour pair for a ticket status,
   * matching the colour scheme used on the schedule view. Falls back to undefined
   * if the status isn't configured (the SCSS palette will then take over).
   */
  ticketColour(
    status: string | undefined
  ): { background: string; foreground: string } | null {
    if (!status) return null;
    const colours = this.clientConfig?.config?.ticketColours
      ?.status as unknown as
      Record<string, { background: string; foreground: string }> | undefined;
    const entry = colours?.[status];
    if (!entry || !entry.background) return null;
    return entry;
  }

  getInitials(name: string | undefined | null): string {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  }

  // --- duration summary -----------------------------------------------------

  /**
   * Schedule duration is the sum of (active visit-schedule quantity × job-schedule SMV).
   * Archived ghost rows are excluded so the figure reflects what's currently committed
   * to this visit, not its history. Returns 0 when the visit has no live SMVs to sum.
   */
  scheduleDurationMinutes(visit: DesignerVisitDto): number {
    if (!this.layout) return 0;
    const smvById = new Map<number, number>();
    for (const js of this.layout.jobSchedules) {
      if (js.id != null) smvById.set(js.id, js.sorSmv ?? 0);
    }
    let total = 0;
    for (const vs of visit.schedules ?? []) {
      if (vs.archivedGhost) continue;
      const qty = vs.quantity ?? 0;
      const smv = smvById.get(vs.jobScheduleId as number) ?? 0;
      total += qty * smv;
    }
    return total;
  }

  /**
   * Returns the per-ticket "planned" minutes for the planned-duration footer. For
   * pre-despatch tickets the live {@code durationMinutes} IS the plan. From despatch
   * onwards the live value may drift to reflect actual time-on-job, so we prefer the
   * snapshot that was captured at despatch ({@code originalDurationMinutes}). On
   * tickets that were already despatched before the snapshot field existed the
   * snapshot is missing and we report {@code null} - the UI surfaces "Unavailable"
   * rather than a misleading number.
   */
  private plannedMinutesFor(t: DesignerTicketDto): number | null {
    const status = t.ticketStatus as string | undefined;
    const preDispatch = status === "PLANNING" || status === "PINNED";
    if (preDispatch) {
      return t.durationMinutes != null && t.durationMinutes > 0
        ? t.durationMinutes
        : null;
    }
    if (t.originalDurationMinutes != null && t.originalDurationMinutes > 0) {
      return t.originalDurationMinutes;
    }
    return null;
  }

  /**
   * Display string for planned duration. Single value when all contributing tickets
   * carry the same duration (or there's only one), otherwise a "min - max" range.
   * Returns "Unavailable" when at least one non-cancelled ticket is post-despatch
   * but has no snapshot (legacy data); returns null when nothing to show.
   */
  plannedDurationLabel(visit: DesignerVisitDto): string | null {
    const tickets = (visit.tickets ?? []).filter(
      (t) => t.ticketStatus !== ("CANCELLED" as never)
    );
    if (tickets.length === 0) return null;
    const mins: number[] = [];
    let anyMissing = false;
    for (const t of tickets) {
      const m = this.plannedMinutesFor(t);
      if (m == null) {
        anyMissing = true;
      } else {
        mins.push(m);
      }
    }
    if (mins.length === 0) {
      return anyMissing ? "Unavailable" : null;
    }
    const lo = Math.min(...mins);
    const hi = Math.max(...mins);
    const range = lo === hi ? `${lo} min` : `${lo}\u2013${hi} min`;
    // Mixed-state visit: some tickets have a snapshot, others don't. Be honest about it.
    return anyMissing ? `${range} (some unavailable)` : range;
  }

  /**
   * Display string for actual duration based on completed tickets only. We compute
   * (completedTime - startedTime) per ticket and present the same single-value /
   * range treatment as planned duration. The COMPLETE gate matters: the solver
   * writes its *scheduled* slot times into startedTime/completedTime on every
   * booking solve, so on a booked-but-unworked ticket those timestamps describe
   * the plan (one solve behind, at that - see #1459), not work done. Only at
   * COMPLETE do they hold operative-reported actuals. Returns null when no
   * ticket has completed yet.
   */
  actualDurationLabel(visit: DesignerVisitDto): string | null {
    const durations: number[] = [];
    for (const t of visit.tickets ?? []) {
      if (t.ticketStatus !== "COMPLETE") continue;
      if (!t.startedTime || !t.completedTime) continue;
      const start = new Date(t.startedTime as unknown as string).getTime();
      const end = new Date(t.completedTime as unknown as string).getTime();
      if (!isFinite(start) || !isFinite(end) || end < start) continue;
      durations.push(Math.round((end - start) / 60000));
    }
    if (durations.length === 0) return null;
    const lo = Math.min(...durations);
    const hi = Math.max(...durations);
    return lo === hi ? `${lo} min` : `${lo}\u2013${hi} min`;
  }

  /**
   * Open the standard ticket-action dialog for the given ticket.Keeps the designer board
   * consistent with everywhere else in the app that surfaces a ticket - the dialog handles
   * its own data fetch from {@code ticketId}.
   */
  openTicket(ticket: DesignerTicketDto): void {
    if (ticket.id == null) return;
    const ref = this.dialog.open(TicketActionDialogComponent, {
      data: { ticketId: ticket.id }
    });
    // Belt and braces alongside the STOMP subscription: the dialog can change the ticket
    // (resource lock, duration, recall) and the websocket may be down or lagging.
    ref.afterClosed().subscribe(() => {
      if (!this.hasPending() && !this.fetchingLayout && !this.applying) {
        void this.refresh(true);
      }
    });
  }

  // --- job-requirement drag onto a lane -----------------------------------

  /**
   * Handle a drop where the source is a job-requirement mini-card from the left panel.
   * Creates (or stacks onto) a visit-schedule row on the target visit using the same
   * quantity dialog as a lane-to-lane move.
   */
  /**
   * Handles a visit-schedule dragged back onto the Job Requirements panel.
   * Removes the schedule from its source visit and returns the quantity to the pool.
   * Only works for non-locked, non-archiving visits (COMPLETED/CANCELLED visits cannot
   * return allocations to the pool — those are part of the audit trail).
   */
  async dropOnJobRequirements(
    event: CdkDragDrop<null, null, DesignerDragPayload>
  ): Promise<void> {
    if (!this.layout) return;
    const payload = event.item.data;
    if (isJobScheduleDrag(payload)) return; // dragged from within the requirements panel itself

    const { schedule, sourceVisit } = payload;
    if (sourceVisit.locked || this.sourceWillArchive(sourceVisit)) return;

    const qty = schedule.quantity ?? 0;
    if (qty <= 0) return;

    const js = this.layout.jobSchedules.find(
      (j) => j.id === schedule.jobScheduleId
    );
    if (!js) return;

    // Remove from the source visit optimistically.
    sourceVisit.schedules = sourceVisit.schedules.filter((s) => s !== schedule);

    if (schedule.id != null) {
      // Server-persisted schedule — delete it on apply.
      this.scheduleOps.push({ op: "DELETE", visitScheduleId: schedule.id });
    } else {
      // Staged-only schedule (never applied) — cancel its pending create op instead.
      const opIdx = this.scheduleOps.findIndex(
        (o) =>
          o.op === "SET_QUANTITY" &&
          o.jobScheduleId === schedule.jobScheduleId &&
          (o.targetVisitId != null
            ? o.targetVisitId === sourceVisit.id
            : o.targetVisitClientRef != null &&
              o.targetVisitClientRef === sourceVisit.clientRef)
      );
      if (opIdx >= 0) {
        this.scheduleOps.splice(opIdx, 1);
      }
    }

    // Return the quantity to the pool.
    js.allocatedQuantity -= qty;
  }

  async dropJobRequirement(
    target: DesignerVisitDto,
    js: DesignerJobScheduleDto
  ): Promise<void> {
    if (!this.layout) return;
    if (target.locked) {
      this.snackBar.open("Target visit is locked.", "Dismiss", {
        duration: 3000
      });
      return;
    }
    const remaining = js.allowedQuantity - js.allocatedQuantity;
    if (remaining <= QUANTITY_EPSILON) {
      this.snackBar.open(
        "No remaining requirement to allocate for this SOR.",
        "Dismiss",
        { duration: 3000 }
      );
      return;
    }
    // Reuse the quantity dialog by faking a "ghost" source schedule carrying just enough
    // shape for the dialog to compute its max - source visit semantics aren't relevant here
    // because we never archive when the source is the job-requirement panel.
    const ghostSource: DesignerVisitScheduleDto = {
      jobScheduleId: js.id,
      sorCode: js.sorCode,
      sorDescription: js.sorDescription,
      sorSmv: js.sorSmv,
      externalScheduleReference: js.externalScheduleReference,
      quantity: remaining
    };
    const ghostSourceVisit: DesignerVisitDto = {
      visitStatus: "NEW",
      locked: false,
      schedules: [ghostSource]
    };
    const data: QuantityDragDialogData = {
      source: ghostSourceVisit,
      target,
      schedule: ghostSource,
      sourceWillArchive: false
    };
    const ref = this.dialog.open<
      QuantityDragDialogComponent,
      QuantityDragDialogData,
      QuantityDragDialogResult
    >(QuantityDragDialogComponent, { data });
    const result = await firstValueFrom(ref.afterClosed());
    if (!result) return;

    const moved = result.quantity;
    const existing = target.schedules.find(
      (s) => !s.archivedGhost && s.jobScheduleId === js.id && s.id != null
    );
    if (existing) {
      existing.quantity = (existing.quantity ?? 0) + moved;
      this.scheduleOps.push({
        op: "SET_QUANTITY",
        visitScheduleId: existing.id,
        quantity: existing.quantity
      });
    } else {
      const newRow: DesignerVisitScheduleDto = {
        jobScheduleId: js.id,
        visitId: target.id,
        sorCode: js.sorCode,
        sorDescription: js.sorDescription,
        sorSmv: js.sorSmv,
        externalScheduleReference: js.externalScheduleReference,
        quantity: moved
      };
      target.schedules.push(newRow);
      this.scheduleOps.push({
        op: "SET_QUANTITY",
        jobScheduleId: js.id,
        targetVisitId: target.id,
        targetVisitClientRef: target.clientRef,
        quantity: moved
      });
    }
    // Optimistically reflect allocation in the left panel totals.
    js.allocatedQuantity += moved;
  }
}
